import type { Language } from "../../../types/i18n";

// Part of the healthMore module (see ../healthMore.ts). English is the
// reference; every other language must carry the same keys.
//
// Prefixes: `appt` (appointments and the tab bar shared by the
// appointment/check-in/insurance pages), `checkin`, `ins` (insurance
// requests), `immu` (immunizations and the certificate views).
const en = {
  // Tab bar shared by the health sub-pages
  apptTabEmergency: "Emergency Profile",
  apptTabAppointments: "Appointments",
  apptTabCheckin: "Check-in",
  apptTabInsurance: "Insurance",
  apptOptionalHint: "(optional)",

  // Appointments
  apptTitle: "Appointments",
  apptDescription: "Manage your medical appointments",
  apptLoadFailed: "Failed to load appointments",
  apptBook: "Book Appointment",
  apptUpcomingHeading: "Upcoming Appointments ({count})",
  apptPastHeading: "Past Appointments ({count})",
  apptNoUpcoming: "No upcoming appointments",
  apptBookFirst: "Book your first appointment",
  apptStatusUpcoming: "Upcoming",
  apptStatusCompleted: "Completed",
  apptNote: "Note: {note}",
  apptReschedule: "Reschedule",
  apptType: "Appointment Type",
  apptTypeCheckup: "General Checkup",
  apptTypeTravel: "Travel Consultation",
  apptTypeVaccination: "Vaccination",
  apptTypeFollowUp: "Follow-up",
  apptDate: "Date",
  apptTime: "Time",
  apptNotes: "Notes (optional)",
  apptNotesPlaceholder: "Any special requirements or notes...",
  apptComingSoon: "Appointment booking is coming soon!",

  // Travel check-in
  checkinTitle: "Travel Check-in",
  checkinDescription: "Share emergency health data at travel check-in",
  checkinOperatorCruise: "Cruise ship",
  checkinOperatorAirline: "Airline",
  checkinOperatorHotel: "Hotel",
  checkinScopeBase: "Basic data",
  checkinScopeBaseDesc: "Name, blood type, date of birth",
  checkinScopeEmergency: "Emergency data",
  checkinScopeEmergencyDesc:
    "Allergies, critical medications, emergency contact",
  checkinScopeVaccinations: "Vaccination status",
  checkinScopeVaccinationsDesc: "Valid vaccinations (for entry)",
  checkinExpiry24h: "24 hours",
  checkinExpiry72h: "72 hours",
  checkinExpiry7d: "7 days",
  checkinExpired: "Expired",
  checkinActive: "Active",
  checkinTimeLeft: "{h}h {m}min remaining",
  checkinSelectScope: "Please select at least one data category",
  checkinQrGenerated: "Check-in QR code generated!",
  checkinQrFailed: "Could not generate the QR code",
  checkinPrivacyTitle: "Privacy first:",
  checkinPrivacyText:
    "You decide which data is shared. Every access is recorded in the audit log.",
  checkinChooseOperator: "Choose operator type",
  checkinContinue: "Continue to setup",
  checkinHistory: "Previous check-ins",
  checkinScanned: "Scanned ✓",
  checkinDetails: "{operator} – Details",
  checkinOperatorName: "Operator name",
  checkinHotelPlaceholder: "Hotel name",
  checkinBookingRef: "Booking reference",
  checkinSharedData: "Shared data",
  checkinValidity: "Validity period",
  checkinGenerating: "Generating...",
  checkinGenerate: "Generate QR code",
  checkinQrHeading: "{operator} Check-in QR",
  checkinBookingRefShort: "Booking ref: {ref}",
  checkinQrAlt: "Check-in QR code",
  checkinCopied: "Copied!",
  checkinCopyLink: "Copy link",
  checkinStaffScans:
    "Check-in staff scan this code. The access is recorded in the audit log.",
  checkinCompleted: "Check-in completed!",
  checkinConfirm: "Confirm check-in",
  checkinSuccess: "Check-in successful!",
  checkinSharedWith: "Your health data has been shared with {operator}.",
  checkinAccessExpires: "Access expires on {date}.",
  checkinNew: "New check-in",
  checkinToAppointments: "Go to appointments",

  // Insurance requests
  insTitle: "Insurance Requests",
  insDescription: "Request repatriation and insurance benefits",
  insTypeRepatriation: "Repatriation",
  insTypeTreatment: "Medical treatment",
  insTypePrescription: "Prescription / medication",
  insTypeOther: "Other",
  insStatusPending: "Pending",
  insStatusSubmitted: "Submitted",
  insStatusApproved: "Approved",
  insStatusRejected: "Rejected",
  insRequiredError: "Insurer name and description are required",
  insSubmitted: "Insurance request submitted",
  insSubmitFailed: "Could not submit the request",
  insMyRequests: "My requests",
  insRefresh: "Refresh",
  insNew: "New request",
  insFormTitle: "New Insurance Request",
  insRequestType: "Request type",
  insCompany: "Insurance company",
  insCompanyPlaceholder: "e.g. Allianz Travel Insurance",
  insPolicyNumber: "Policy number",
  insDescriptionLabel: "Description",
  insDescriptionPlaceholder: "Describe your situation and what you need...",
  insEmergencyNote:
    "In a real emergency, contact your insurer directly. This feature is for documenting and submitting non-urgent requests.",
  insSubmitting: "Submitting...",
  insSubmit: "Submit request",
  insEmpty: "No requests yet",
  insEmptyHint: "Submit your first insurance request.",
  insReference: "Reference: {ref}",

  // Immunizations
  immuLoadFailed: "Failed to load immunizations",
  immuSaveFailed: "Failed to save immunization",
  immuDeleteFailed: "Failed to delete immunization",
  immuDeleteConfirm:
    "Are you sure you want to delete this immunization record?",
  immuFhirLoadFailed: "Failed to load the digital record",
  immuCertLoadFailed: "Failed to load the international certificate",
  immuUploadFailed: "Failed to upload certificate",
  immuNoCertificate: "No certificate found for this immunization",
  immuDownloadFailed: "Failed to download certificate",
  immuDigitalRecord: "Digital Record",
  immuDigitalRecordTitle: "Digital immunization record",
  immuVaccineName: "Vaccine Name",
  immuVaccineNamePlaceholder: "e.g. COVID-19 mRNA Vaccine",
  immuVaccineCodePlaceholder: "e.g. CVX-218",
  immuManufacturerPlaceholder: "e.g. BioNTech/Pfizer",
  immuLotPlaceholder: "e.g. FF1234",
  immuDoseNumber: "Dose Number",
  immuTotalDoses: "Total Doses",
  immuAdminDate: "Administration Date",
  immuExpirationDate: "Expiration Date",
  immuNextDueDate: "Next Due Date",
  immuAdminLocation: "Administration Location",
  immuLocationPlaceholder: "e.g. Family Practice Dr. Smith",
  immuAdministeredBy: "Administered By",
  immuAdministratorPlaceholder: "e.g. Dr. Smith, MD",
  immuEmptyTitle: "No immunizations found",
  immuEmptyHint: "Add your first immunization record",
  immuAddFirst: "Add First Immunization",
  immuDoseOf: "Dose {dose} of {total}",
  immuLot: "Lot: {lot}",
  immuNextDue: "Next dose due: {date}",
  immuDownloadCert: "Download certificate",
  immuUploadCert: "Upload certificate",
  immuUploadTitle: "Upload Certificate",
  immuCertFileLabel: "Vaccination certificate (PDF, JPEG, PNG, WebP, HEIC)",
  immuUploading: "Uploading...",
  immuUpload: "Upload",
  immuFhirBundleTitle: "FHIR R5 Immunization Bundle",
  immuRecordCopied: "Record copied to clipboard!",
  immuCopyToClipboard: "Copy to clipboard",
  immuCertCopied: "Certificate data copied to clipboard!",
  immuName: "Name",
  immuDateOfBirth: "Date of birth",
  immuSex: "Sex",
  immuSexMale: "Male",
  immuSexFemale: "Female",
  immuSexOther: "Other",
  immuSexUnknown: "Unknown",
  immuAlgorithm: "Algorithm",
  immuSignature: "Signature",
  immuVerified: "Verified",
  immuSignedBy: "Signed by {issuer}",
  immuUnsigned: "Unsigned – this server has no signing key configured",
  immuNotAvailable: "N/A",
  immuNotSpecified: "Not specified",
  // Certificate upload modal
  immuUploadVaccCertTitle: "Upload Vaccination Certificate",
  immuFileTypeError: "Only PDF, PNG and JPEG files are allowed",
  immuFileTooLarge: "File too large. Maximum 10 MB allowed",
  immuUploadSuccessTitle: "Upload successful!",
  immuUploadSuccessText: "The certificate has been uploaded successfully.",
  immuCertFile: "Certificate file",
  immuSelectFile: "Select file",
  immuFileHint: "PDF, PNG or JPEG (max. 10 MB)",
  // FHIR record modal
  immuFhirTitle: "Digital Immunization Record",
  immuBundleInfo: "Bundle Information",
  immuResourceType: "Resource type",
  immuBundleType: "Type",
  immuTotalEntries: "Total entries",
  immuTimestamp: "Timestamp",
  immuIdentifiers: "Identifiers",
  immuStatus: "Status",
  immuStatusCompleted: "Completed",
  immuStatusNotDone: "Not done",
  immuStatusEnteredInError: "Entered in error",
  immuVaccine: "Vaccine",
  immuPerformedBy: "Performed by",
  immuSchedule: "Vaccination schedule",
  immuNotes: "Notes",
  immuRecordedOn: "Recorded on: {date}",
  immuShowRawJson: "Show raw FHIR JSON",
  // International certificate modal
  immuDate: "Date",
  immuPhysician: "Physician",
  immuNextVaccination: "Next vaccination",
  immuDigitalVerification: "Digital verification",
  immuQrVerifyHint: "QR code for digital verification of the certificate",
  immuIssuingAuthority: "Issuing authority",
  immuOrganization: "Organization",
  immuValidUntil: "Valid until",
  immuSignatureId: "Signature ID",
  immuSignedOn: "Signed on",
  immuLegalNoticeTitle: "Legal notice",
  immuLegalNoticeText:
    "This certificate complies with the WHO International Health Regulations (IHR 2005). Its authenticity can be verified via the QR code or the certificate number.",
  immuCreatedOn: "Created on: {date}",
  immuDownload: "Download",
};

const de: typeof en = {
  apptTabEmergency: "Notfallprofil",
  apptTabAppointments: "Termine",
  apptTabCheckin: "Check-in",
  apptTabInsurance: "Versicherung",
  apptOptionalHint: "(optional)",

  apptTitle: "Termine",
  apptDescription: "Verwalten Sie Ihre Arzttermine",
  apptLoadFailed: "Termine konnten nicht geladen werden",
  apptBook: "Termin buchen",
  apptUpcomingHeading: "Anstehende Termine ({count})",
  apptPastHeading: "Vergangene Termine ({count})",
  apptNoUpcoming: "Keine anstehenden Termine",
  apptBookFirst: "Ersten Termin buchen",
  apptStatusUpcoming: "Anstehend",
  apptStatusCompleted: "Abgeschlossen",
  apptNote: "Notiz: {note}",
  apptReschedule: "Verschieben",
  apptType: "Terminart",
  apptTypeCheckup: "Allgemeine Untersuchung",
  apptTypeTravel: "Reisemedizinische Beratung",
  apptTypeVaccination: "Impfung",
  apptTypeFollowUp: "Nachsorge",
  apptDate: "Datum",
  apptTime: "Uhrzeit",
  apptNotes: "Notizen (optional)",
  apptNotesPlaceholder: "Besondere Wünsche oder Hinweise...",
  apptComingSoon: "Die Terminbuchung ist bald verfügbar!",

  checkinTitle: "Reise-Check-in",
  checkinDescription:
    "Übergabe von Notfall-Gesundheitsdaten beim Reise-Check-in",
  checkinOperatorCruise: "Kreuzfahrtschiff",
  checkinOperatorAirline: "Fluggesellschaft",
  checkinOperatorHotel: "Hotel",
  checkinScopeBase: "Basisdaten",
  checkinScopeBaseDesc: "Name, Blutgruppe, Geburtsdatum",
  checkinScopeEmergency: "Notfalldaten",
  checkinScopeEmergencyDesc: "Allergien, kritische Medikamente, Notfallkontakt",
  checkinScopeVaccinations: "Impfstatus",
  checkinScopeVaccinationsDesc: "Gültige Impfungen (für die Einreise)",
  checkinExpiry24h: "24 Stunden",
  checkinExpiry72h: "72 Stunden",
  checkinExpiry7d: "7 Tage",
  checkinExpired: "Abgelaufen",
  checkinActive: "Aktiv",
  checkinTimeLeft: "{h} Std. {m} Min. verbleibend",
  checkinSelectScope: "Bitte mindestens eine Datenkategorie auswählen",
  checkinQrGenerated: "Check-in-QR-Code erstellt!",
  checkinQrFailed: "QR-Code konnte nicht erstellt werden",
  checkinPrivacyTitle: "DSGVO-konform:",
  checkinPrivacyText:
    "Sie bestimmen, welche Daten übermittelt werden. Alle Zugriffe werden im Audit-Log gespeichert.",
  checkinChooseOperator: "Betreibertyp wählen",
  checkinContinue: "Weiter konfigurieren",
  checkinHistory: "Bisherige Check-ins",
  checkinScanned: "Gescannt ✓",
  checkinDetails: "{operator} – Details",
  checkinOperatorName: "Betreibername",
  checkinHotelPlaceholder: "Name des Hotels",
  checkinBookingRef: "Buchungsreferenz",
  checkinSharedData: "Freigegebene Daten",
  checkinValidity: "Gültigkeitsdauer",
  checkinGenerating: "Wird erstellt...",
  checkinGenerate: "QR-Code erstellen",
  checkinQrHeading: "{operator} Check-in-QR",
  checkinBookingRefShort: "Buchungsref.: {ref}",
  checkinQrAlt: "Check-in-QR-Code",
  checkinCopied: "Kopiert!",
  checkinCopyLink: "Link kopieren",
  checkinStaffScans:
    "Das Check-in-Personal scannt diesen Code. Der Zugriff wird im Audit-Log gespeichert.",
  checkinCompleted: "Check-in abgeschlossen!",
  checkinConfirm: "Check-in bestätigen",
  checkinSuccess: "Check-in erfolgreich!",
  checkinSharedWith: "Ihre Gesundheitsdaten wurden für {operator} freigegeben.",
  checkinAccessExpires: "Der Zugriff läuft am {date} ab.",
  checkinNew: "Neuer Check-in",
  checkinToAppointments: "Zu den Terminen",

  insTitle: "Versicherungsanfragen",
  insDescription: "Rücktransport und Versicherungsleistungen beantragen",
  insTypeRepatriation: "Rücktransport",
  insTypeTreatment: "Medizinische Behandlung",
  insTypePrescription: "Rezept / Medikamente",
  insTypeOther: "Sonstiges",
  insStatusPending: "Ausstehend",
  insStatusSubmitted: "Eingereicht",
  insStatusApproved: "Genehmigt",
  insStatusRejected: "Abgelehnt",
  insRequiredError: "Versicherer und Beschreibung sind Pflichtfelder",
  insSubmitted: "Versicherungsanfrage eingereicht",
  insSubmitFailed: "Fehler beim Einreichen",
  insMyRequests: "Meine Anfragen",
  insRefresh: "Aktualisieren",
  insNew: "Neue Anfrage",
  insFormTitle: "Neue Versicherungsanfrage",
  insRequestType: "Art der Anfrage",
  insCompany: "Versicherungsunternehmen",
  insCompanyPlaceholder: "z. B. Allianz Reiseversicherung",
  insPolicyNumber: "Policennummer",
  insDescriptionLabel: "Beschreibung",
  insDescriptionPlaceholder:
    "Beschreiben Sie Ihre Situation und was Sie benötigen...",
  insEmergencyNote:
    "In einem echten Notfall kontaktieren Sie Ihre Versicherung bitte direkt. Diese Funktion dient der Dokumentation und Einreichung nicht-dringlicher Anfragen.",
  insSubmitting: "Wird eingereicht...",
  insSubmit: "Anfrage einreichen",
  insEmpty: "Keine Anfragen vorhanden",
  insEmptyHint: "Reichen Sie Ihre erste Versicherungsanfrage ein.",
  insReference: "Referenz: {ref}",

  immuLoadFailed: "Impfungen konnten nicht geladen werden",
  immuSaveFailed: "Impfung konnte nicht gespeichert werden",
  immuDeleteFailed: "Impfung konnte nicht gelöscht werden",
  immuDeleteConfirm: "Möchten Sie diesen Impfeintrag wirklich löschen?",
  immuFhirLoadFailed: "Der digitale Impfnachweis konnte nicht geladen werden",
  immuCertLoadFailed:
    "Das internationale Zertifikat konnte nicht geladen werden",
  immuUploadFailed: "Zertifikat konnte nicht hochgeladen werden",
  immuNoCertificate: "Für diese Impfung ist kein Zertifikat vorhanden",
  immuDownloadFailed: "Zertifikat konnte nicht heruntergeladen werden",
  immuDigitalRecord: "Digitaler Nachweis",
  immuDigitalRecordTitle: "Digitaler Impfnachweis",
  immuVaccineName: "Impfstoff",
  immuVaccineNamePlaceholder: "z. B. COVID-19-mRNA-Impfstoff",
  immuVaccineCodePlaceholder: "z. B. CVX-218",
  immuManufacturerPlaceholder: "z. B. BioNTech/Pfizer",
  immuLotPlaceholder: "z. B. FF1234",
  immuDoseNumber: "Dosis-Nr.",
  immuTotalDoses: "Dosen insgesamt",
  immuAdminDate: "Impfdatum",
  immuExpirationDate: "Ablaufdatum",
  immuNextDueDate: "Nächste Fälligkeit",
  immuAdminLocation: "Impfort",
  immuLocationPlaceholder: "z. B. Hausarztpraxis Dr. Schmidt",
  immuAdministeredBy: "Geimpft von",
  immuAdministratorPlaceholder: "z. B. Dr. med. Schmidt",
  immuEmptyTitle: "Keine Impfungen gefunden",
  immuEmptyHint: "Fügen Sie Ihren ersten Impfeintrag hinzu",
  immuAddFirst: "Erste Impfung hinzufügen",
  immuDoseOf: "Dosis {dose} von {total}",
  immuLot: "Charge: {lot}",
  immuNextDue: "Nächste Dosis fällig: {date}",
  immuDownloadCert: "Zertifikat herunterladen",
  immuUploadCert: "Zertifikat hochladen",
  immuUploadTitle: "Zertifikat hochladen",
  immuCertFileLabel: "Impfzertifikat (PDF, JPEG, PNG, WebP, HEIC)",
  immuUploading: "Wird hochgeladen...",
  immuUpload: "Hochladen",
  immuFhirBundleTitle: "FHIR-R5-Impf-Bundle",
  immuRecordCopied: "Nachweis in die Zwischenablage kopiert!",
  immuCopyToClipboard: "In die Zwischenablage kopieren",
  immuCertCopied: "Zertifikatsdaten in die Zwischenablage kopiert!",
  immuName: "Name",
  immuDateOfBirth: "Geburtsdatum",
  immuSex: "Geschlecht",
  immuSexMale: "Männlich",
  immuSexFemale: "Weiblich",
  immuSexOther: "Divers",
  immuSexUnknown: "Unbekannt",
  immuAlgorithm: "Algorithmus",
  immuSignature: "Signatur",
  immuVerified: "Verifiziert",
  immuSignedBy: "Signiert von {issuer}",
  immuUnsigned:
    "Nicht signiert – auf diesem Server ist kein Signaturschlüssel eingerichtet",
  immuNotAvailable: "k. A.",
  immuNotSpecified: "Nicht angegeben",
  immuUploadVaccCertTitle: "Impfzertifikat hochladen",
  immuFileTypeError: "Nur PDF-, PNG- und JPEG-Dateien sind erlaubt",
  immuFileTooLarge: "Datei zu groß. Maximal 10 MB erlaubt",
  immuUploadSuccessTitle: "Hochladen erfolgreich!",
  immuUploadSuccessText: "Das Zertifikat wurde erfolgreich hochgeladen.",
  immuCertFile: "Zertifikatsdatei",
  immuSelectFile: "Datei auswählen",
  immuFileHint: "PDF, PNG oder JPEG (max. 10 MB)",
  immuFhirTitle: "Digitaler Impfnachweis",
  immuBundleInfo: "Bundle-Informationen",
  immuResourceType: "Ressourcentyp",
  immuBundleType: "Typ",
  immuTotalEntries: "Einträge insgesamt",
  immuTimestamp: "Zeitstempel",
  immuIdentifiers: "Identifikatoren",
  immuStatus: "Status",
  immuStatusCompleted: "Abgeschlossen",
  immuStatusNotDone: "Nicht durchgeführt",
  immuStatusEnteredInError: "Irrtümlich erfasst",
  immuVaccine: "Impfstoff",
  immuPerformedBy: "Durchgeführt von",
  immuSchedule: "Impfschema",
  immuNotes: "Notizen",
  immuRecordedOn: "Erfasst am: {date}",
  immuShowRawJson: "FHIR-Rohdaten (JSON) anzeigen",
  immuDate: "Datum",
  immuPhysician: "Arzt/Ärztin",
  immuNextVaccination: "Nächste Impfung",
  immuDigitalVerification: "Digitale Verifikation",
  immuQrVerifyHint: "QR-Code zur digitalen Verifikation des Zertifikats",
  immuIssuingAuthority: "Ausstellende Behörde",
  immuOrganization: "Organisation",
  immuValidUntil: "Gültig bis",
  immuSignatureId: "Signatur-ID",
  immuSignedOn: "Signiert am",
  immuLegalNoticeTitle: "Rechtlicher Hinweis",
  immuLegalNoticeText:
    "Dieses Zertifikat entspricht den Internationalen Gesundheitsvorschriften (IGV 2005) der WHO. Die Echtheit kann über den QR-Code oder die Zertifikatsnummer verifiziert werden.",
  immuCreatedOn: "Erstellt am: {date}",
  immuDownload: "Herunterladen",
};

const fr: typeof en = {
  apptTabEmergency: "Profil d'urgence",
  apptTabAppointments: "Rendez-vous",
  apptTabCheckin: "Enregistrement",
  apptTabInsurance: "Assurance",
  apptOptionalHint: "(facultatif)",

  apptTitle: "Rendez-vous",
  apptDescription: "Gérez vos rendez-vous médicaux",
  apptLoadFailed: "Impossible de charger les rendez-vous",
  apptBook: "Prendre rendez-vous",
  apptUpcomingHeading: "Rendez-vous à venir ({count})",
  apptPastHeading: "Rendez-vous passés ({count})",
  apptNoUpcoming: "Aucun rendez-vous à venir",
  apptBookFirst: "Prendre votre premier rendez-vous",
  apptStatusUpcoming: "À venir",
  apptStatusCompleted: "Terminé",
  apptNote: "Remarque : {note}",
  apptReschedule: "Reporter",
  apptType: "Type de rendez-vous",
  apptTypeCheckup: "Bilan de santé",
  apptTypeTravel: "Consultation voyage",
  apptTypeVaccination: "Vaccination",
  apptTypeFollowUp: "Suivi",
  apptDate: "Date",
  apptTime: "Heure",
  apptNotes: "Remarques (facultatif)",
  apptNotesPlaceholder: "Besoins particuliers ou remarques...",
  apptComingSoon: "La prise de rendez-vous arrive bientôt !",

  checkinTitle: "Enregistrement voyage",
  checkinDescription:
    "Transmettre vos données de santé d'urgence lors de l'enregistrement",
  checkinOperatorCruise: "Navire de croisière",
  checkinOperatorAirline: "Compagnie aérienne",
  checkinOperatorHotel: "Hôtel",
  checkinScopeBase: "Données de base",
  checkinScopeBaseDesc: "Nom, groupe sanguin, date de naissance",
  checkinScopeEmergency: "Données d'urgence",
  checkinScopeEmergencyDesc:
    "Allergies, médicaments critiques, contact d'urgence",
  checkinScopeVaccinations: "Statut vaccinal",
  checkinScopeVaccinationsDesc: "Vaccinations valides (pour l'entrée)",
  checkinExpiry24h: "24 heures",
  checkinExpiry72h: "72 heures",
  checkinExpiry7d: "7 jours",
  checkinExpired: "Expiré",
  checkinActive: "Actif",
  checkinTimeLeft: "{h} h {m} min restantes",
  checkinSelectScope: "Veuillez sélectionner au moins une catégorie de données",
  checkinQrGenerated: "QR code d'enregistrement généré !",
  checkinQrFailed: "Impossible de générer le QR code",
  checkinPrivacyTitle: "Conforme au RGPD :",
  checkinPrivacyText:
    "Vous décidez quelles données sont transmises. Chaque accès est consigné dans le journal d'audit.",
  checkinChooseOperator: "Choisir le type d'opérateur",
  checkinContinue: "Continuer la configuration",
  checkinHistory: "Enregistrements précédents",
  checkinScanned: "Scanné ✓",
  checkinDetails: "{operator} – Détails",
  checkinOperatorName: "Nom de l'opérateur",
  checkinHotelPlaceholder: "Nom de l'hôtel",
  checkinBookingRef: "Référence de réservation",
  checkinSharedData: "Données partagées",
  checkinValidity: "Durée de validité",
  checkinGenerating: "Génération...",
  checkinGenerate: "Générer le QR code",
  checkinQrHeading: "QR d'enregistrement – {operator}",
  checkinBookingRefShort: "Réf. réservation : {ref}",
  checkinQrAlt: "QR code d'enregistrement",
  checkinCopied: "Copié !",
  checkinCopyLink: "Copier le lien",
  checkinStaffScans:
    "Le personnel d'enregistrement scanne ce code. L'accès est consigné dans le journal d'audit.",
  checkinCompleted: "Enregistrement terminé !",
  checkinConfirm: "Confirmer l'enregistrement",
  checkinSuccess: "Enregistrement réussi !",
  checkinSharedWith: "Vos données de santé ont été partagées avec {operator}.",
  checkinAccessExpires: "L'accès expire le {date}.",
  checkinNew: "Nouvel enregistrement",
  checkinToAppointments: "Aller aux rendez-vous",

  insTitle: "Demandes d'assurance",
  insDescription: "Demander un rapatriement et des prestations d'assurance",
  insTypeRepatriation: "Rapatriement",
  insTypeTreatment: "Traitement médical",
  insTypePrescription: "Ordonnance / médicaments",
  insTypeOther: "Autre",
  insStatusPending: "En attente",
  insStatusSubmitted: "Soumise",
  insStatusApproved: "Approuvée",
  insStatusRejected: "Refusée",
  insRequiredError: "Le nom de l'assureur et la description sont obligatoires",
  insSubmitted: "Demande d'assurance envoyée",
  insSubmitFailed: "Impossible d'envoyer la demande",
  insMyRequests: "Mes demandes",
  insRefresh: "Actualiser",
  insNew: "Nouvelle demande",
  insFormTitle: "Nouvelle demande d'assurance",
  insRequestType: "Type de demande",
  insCompany: "Compagnie d'assurance",
  insCompanyPlaceholder: "p. ex. AXA Assistance",
  insPolicyNumber: "Numéro de police",
  insDescriptionLabel: "Description",
  insDescriptionPlaceholder: "Décrivez votre situation et vos besoins...",
  insEmergencyNote:
    "En cas d'urgence réelle, contactez directement votre assureur. Cette fonction sert à documenter et soumettre des demandes non urgentes.",
  insSubmitting: "Envoi en cours...",
  insSubmit: "Envoyer la demande",
  insEmpty: "Aucune demande",
  insEmptyHint: "Envoyez votre première demande d'assurance.",
  insReference: "Référence : {ref}",

  immuLoadFailed: "Impossible de charger les vaccinations",
  immuSaveFailed: "Impossible d'enregistrer la vaccination",
  immuDeleteFailed: "Impossible de supprimer la vaccination",
  immuDeleteConfirm: "Voulez-vous vraiment supprimer cette vaccination ?",
  immuFhirLoadFailed: "Impossible de charger le carnet numérique",
  immuCertLoadFailed: "Impossible de charger le certificat international",
  immuUploadFailed: "Impossible de téléverser le certificat",
  immuNoCertificate: "Aucun certificat trouvé pour cette vaccination",
  immuDownloadFailed: "Impossible de télécharger le certificat",
  immuDigitalRecord: "Carnet numérique",
  immuDigitalRecordTitle: "Carnet de vaccination numérique",
  immuVaccineName: "Nom du vaccin",
  immuVaccineNamePlaceholder: "p. ex. vaccin à ARNm COVID-19",
  immuVaccineCodePlaceholder: "p. ex. CVX-218",
  immuManufacturerPlaceholder: "p. ex. BioNTech/Pfizer",
  immuLotPlaceholder: "p. ex. FF1234",
  immuDoseNumber: "N° de dose",
  immuTotalDoses: "Nombre total de doses",
  immuAdminDate: "Date d'administration",
  immuExpirationDate: "Date d'expiration",
  immuNextDueDate: "Prochaine échéance",
  immuAdminLocation: "Lieu d'administration",
  immuLocationPlaceholder: "p. ex. Cabinet du Dr Martin",
  immuAdministeredBy: "Administré par",
  immuAdministratorPlaceholder: "p. ex. Dr Martin",
  immuEmptyTitle: "Aucune vaccination trouvée",
  immuEmptyHint: "Ajoutez votre première vaccination",
  immuAddFirst: "Ajouter une première vaccination",
  immuDoseOf: "Dose {dose} sur {total}",
  immuLot: "Lot : {lot}",
  immuNextDue: "Prochaine dose prévue : {date}",
  immuDownloadCert: "Télécharger le certificat",
  immuUploadCert: "Téléverser le certificat",
  immuUploadTitle: "Téléverser le certificat",
  immuCertFileLabel: "Certificat de vaccination (PDF, JPEG, PNG, WebP, HEIC)",
  immuUploading: "Téléversement...",
  immuUpload: "Téléverser",
  immuFhirBundleTitle: "Bundle de vaccination FHIR R5",
  immuRecordCopied: "Carnet copié dans le presse-papiers !",
  immuCopyToClipboard: "Copier dans le presse-papiers",
  immuCertCopied: "Données du certificat copiées dans le presse-papiers !",
  immuName: "Nom",
  immuDateOfBirth: "Date de naissance",
  immuSex: "Sexe",
  immuSexMale: "Masculin",
  immuSexFemale: "Féminin",
  immuSexOther: "Autre",
  immuSexUnknown: "Inconnu",
  immuAlgorithm: "Algorithme",
  immuSignature: "Signature",
  immuVerified: "Vérifié",
  immuSignedBy: "Signé par {issuer}",
  immuUnsigned:
    "Non signé – aucune clé de signature n'est configurée sur ce serveur",
  immuNotAvailable: "N/D",
  immuNotSpecified: "Non renseigné",
  immuUploadVaccCertTitle: "Téléverser un certificat de vaccination",
  immuFileTypeError: "Seuls les fichiers PDF, PNG et JPEG sont autorisés",
  immuFileTooLarge: "Fichier trop volumineux. 10 Mo maximum",
  immuUploadSuccessTitle: "Téléversement réussi !",
  immuUploadSuccessText: "Le certificat a bien été téléversé.",
  immuCertFile: "Fichier du certificat",
  immuSelectFile: "Choisir un fichier",
  immuFileHint: "PDF, PNG ou JPEG (10 Mo max.)",
  immuFhirTitle: "Carnet de vaccination numérique",
  immuBundleInfo: "Informations du bundle",
  immuResourceType: "Type de ressource",
  immuBundleType: "Type",
  immuTotalEntries: "Nombre d'entrées",
  immuTimestamp: "Horodatage",
  immuIdentifiers: "Identifiants",
  immuStatus: "Statut",
  immuStatusCompleted: "Effectuée",
  immuStatusNotDone: "Non effectuée",
  immuStatusEnteredInError: "Saisie erronée",
  immuVaccine: "Vaccin",
  immuPerformedBy: "Réalisé par",
  immuSchedule: "Schéma vaccinal",
  immuNotes: "Remarques",
  immuRecordedOn: "Enregistré le : {date}",
  immuShowRawJson: "Afficher le JSON FHIR brut",
  immuDate: "Date",
  immuPhysician: "Médecin",
  immuNextVaccination: "Prochaine vaccination",
  immuDigitalVerification: "Vérification numérique",
  immuQrVerifyHint: "QR code pour la vérification numérique du certificat",
  immuIssuingAuthority: "Autorité émettrice",
  immuOrganization: "Organisation",
  immuValidUntil: "Valable jusqu'au",
  immuSignatureId: "ID de signature",
  immuSignedOn: "Signé le",
  immuLegalNoticeTitle: "Mention légale",
  immuLegalNoticeText:
    "Ce certificat est conforme au Règlement sanitaire international (RSI 2005) de l'OMS. Son authenticité peut être vérifiée via le QR code ou le numéro de certificat.",
  immuCreatedOn: "Créé le : {date}",
  immuDownload: "Télécharger",
};

const es: typeof en = {
  apptTabEmergency: "Perfil de emergencia",
  apptTabAppointments: "Citas",
  apptTabCheckin: "Check-in",
  apptTabInsurance: "Seguro",
  apptOptionalHint: "(opcional)",

  apptTitle: "Citas",
  apptDescription: "Gestione sus citas médicas",
  apptLoadFailed: "No se pudieron cargar las citas",
  apptBook: "Reservar cita",
  apptUpcomingHeading: "Próximas citas ({count})",
  apptPastHeading: "Citas anteriores ({count})",
  apptNoUpcoming: "No hay próximas citas",
  apptBookFirst: "Reserve su primera cita",
  apptStatusUpcoming: "Próxima",
  apptStatusCompleted: "Realizada",
  apptNote: "Nota: {note}",
  apptReschedule: "Reprogramar",
  apptType: "Tipo de cita",
  apptTypeCheckup: "Chequeo general",
  apptTypeTravel: "Consulta de viaje",
  apptTypeVaccination: "Vacunación",
  apptTypeFollowUp: "Seguimiento",
  apptDate: "Fecha",
  apptTime: "Hora",
  apptNotes: "Notas (opcional)",
  apptNotesPlaceholder: "Requisitos especiales o notas...",
  apptComingSoon: "¡La reserva de citas estará disponible pronto!",

  checkinTitle: "Check-in de viaje",
  checkinDescription:
    "Compartir datos de salud de emergencia en el check-in del viaje",
  checkinOperatorCruise: "Crucero",
  checkinOperatorAirline: "Aerolínea",
  checkinOperatorHotel: "Hotel",
  checkinScopeBase: "Datos básicos",
  checkinScopeBaseDesc: "Nombre, grupo sanguíneo, fecha de nacimiento",
  checkinScopeEmergency: "Datos de emergencia",
  checkinScopeEmergencyDesc:
    "Alergias, medicamentos críticos, contacto de emergencia",
  checkinScopeVaccinations: "Estado de vacunación",
  checkinScopeVaccinationsDesc: "Vacunas válidas (para la entrada)",
  checkinExpiry24h: "24 horas",
  checkinExpiry72h: "72 horas",
  checkinExpiry7d: "7 días",
  checkinExpired: "Caducado",
  checkinActive: "Activo",
  checkinTimeLeft: "Quedan {h} h {m} min",
  checkinSelectScope: "Seleccione al menos una categoría de datos",
  checkinQrGenerated: "¡Código QR de check-in generado!",
  checkinQrFailed: "No se pudo generar el código QR",
  checkinPrivacyTitle: "Conforme al RGPD:",
  checkinPrivacyText:
    "Usted decide qué datos se comparten. Cada acceso queda registrado en el registro de auditoría.",
  checkinChooseOperator: "Elija el tipo de operador",
  checkinContinue: "Continuar con la configuración",
  checkinHistory: "Check-ins anteriores",
  checkinScanned: "Escaneado ✓",
  checkinDetails: "{operator} – Detalles",
  checkinOperatorName: "Nombre del operador",
  checkinHotelPlaceholder: "Nombre del hotel",
  checkinBookingRef: "Referencia de reserva",
  checkinSharedData: "Datos compartidos",
  checkinValidity: "Período de validez",
  checkinGenerating: "Generando...",
  checkinGenerate: "Generar código QR",
  checkinQrHeading: "QR de check-in – {operator}",
  checkinBookingRefShort: "Ref. reserva: {ref}",
  checkinQrAlt: "Código QR de check-in",
  checkinCopied: "¡Copiado!",
  checkinCopyLink: "Copiar enlace",
  checkinStaffScans:
    "El personal de check-in escanea este código. El acceso queda registrado en el registro de auditoría.",
  checkinCompleted: "¡Check-in completado!",
  checkinConfirm: "Confirmar check-in",
  checkinSuccess: "¡Check-in realizado con éxito!",
  checkinSharedWith: "Sus datos de salud se han compartido con {operator}.",
  checkinAccessExpires: "El acceso caduca el {date}.",
  checkinNew: "Nuevo check-in",
  checkinToAppointments: "Ir a las citas",

  insTitle: "Solicitudes al seguro",
  insDescription: "Solicite repatriación y prestaciones del seguro",
  insTypeRepatriation: "Repatriación",
  insTypeTreatment: "Tratamiento médico",
  insTypePrescription: "Receta / medicamentos",
  insTypeOther: "Otro",
  insStatusPending: "Pendiente",
  insStatusSubmitted: "Enviada",
  insStatusApproved: "Aprobada",
  insStatusRejected: "Rechazada",
  insRequiredError:
    "El nombre de la aseguradora y la descripción son obligatorios",
  insSubmitted: "Solicitud al seguro enviada",
  insSubmitFailed: "No se pudo enviar la solicitud",
  insMyRequests: "Mis solicitudes",
  insRefresh: "Actualizar",
  insNew: "Nueva solicitud",
  insFormTitle: "Nueva solicitud al seguro",
  insRequestType: "Tipo de solicitud",
  insCompany: "Compañía de seguros",
  insCompanyPlaceholder: "p. ej., Mapfre Asistencia",
  insPolicyNumber: "Número de póliza",
  insDescriptionLabel: "Descripción",
  insDescriptionPlaceholder: "Describa su situación y lo que necesita...",
  insEmergencyNote:
    "En una emergencia real, contacte directamente con su aseguradora. Esta función sirve para documentar y enviar solicitudes no urgentes.",
  insSubmitting: "Enviando...",
  insSubmit: "Enviar solicitud",
  insEmpty: "No hay solicitudes",
  insEmptyHint: "Envíe su primera solicitud al seguro.",
  insReference: "Referencia: {ref}",

  immuLoadFailed: "No se pudieron cargar las vacunas",
  immuSaveFailed: "No se pudo guardar la vacuna",
  immuDeleteFailed: "No se pudo eliminar la vacuna",
  immuDeleteConfirm: "¿Seguro que desea eliminar este registro de vacunación?",
  immuFhirLoadFailed: "No se pudo cargar el registro digital",
  immuCertLoadFailed: "No se pudo cargar el certificado internacional",
  immuUploadFailed: "No se pudo subir el certificado",
  immuNoCertificate: "No hay ningún certificado para esta vacuna",
  immuDownloadFailed: "No se pudo descargar el certificado",
  immuDigitalRecord: "Registro digital",
  immuDigitalRecordTitle: "Registro digital de vacunación",
  immuVaccineName: "Nombre de la vacuna",
  immuVaccineNamePlaceholder: "p. ej., vacuna de ARNm contra la COVID-19",
  immuVaccineCodePlaceholder: "p. ej., CVX-218",
  immuManufacturerPlaceholder: "p. ej., BioNTech/Pfizer",
  immuLotPlaceholder: "p. ej., FF1234",
  immuDoseNumber: "N.º de dosis",
  immuTotalDoses: "Total de dosis",
  immuAdminDate: "Fecha de administración",
  immuExpirationDate: "Fecha de caducidad",
  immuNextDueDate: "Próxima fecha prevista",
  immuAdminLocation: "Lugar de administración",
  immuLocationPlaceholder: "p. ej., Consulta Dr. García",
  immuAdministeredBy: "Administrada por",
  immuAdministratorPlaceholder: "p. ej., Dr. García",
  immuEmptyTitle: "No se encontraron vacunas",
  immuEmptyHint: "Añada su primer registro de vacunación",
  immuAddFirst: "Añadir primera vacuna",
  immuDoseOf: "Dosis {dose} de {total}",
  immuLot: "Lote: {lot}",
  immuNextDue: "Próxima dosis: {date}",
  immuDownloadCert: "Descargar certificado",
  immuUploadCert: "Subir certificado",
  immuUploadTitle: "Subir certificado",
  immuCertFileLabel: "Certificado de vacunación (PDF, JPEG, PNG, WebP, HEIC)",
  immuUploading: "Subiendo...",
  immuUpload: "Subir",
  immuFhirBundleTitle: "Bundle de vacunación FHIR R5",
  immuRecordCopied: "¡Registro copiado al portapapeles!",
  immuCopyToClipboard: "Copiar al portapapeles",
  immuCertCopied: "¡Datos del certificado copiados al portapapeles!",
  immuName: "Nombre",
  immuDateOfBirth: "Fecha de nacimiento",
  immuSex: "Sexo",
  immuSexMale: "Masculino",
  immuSexFemale: "Femenino",
  immuSexOther: "Otro",
  immuSexUnknown: "Desconocido",
  immuAlgorithm: "Algoritmo",
  immuSignature: "Firma",
  immuVerified: "Verificado",
  immuSignedBy: "Firmado por {issuer}",
  immuUnsigned:
    "Sin firmar: este servidor no tiene una clave de firma configurada",
  immuNotAvailable: "N/D",
  immuNotSpecified: "No indicado",
  immuUploadVaccCertTitle: "Subir certificado de vacunación",
  immuFileTypeError: "Solo se permiten archivos PDF, PNG y JPEG",
  immuFileTooLarge: "Archivo demasiado grande. Máximo 10 MB",
  immuUploadSuccessTitle: "¡Subida correcta!",
  immuUploadSuccessText: "El certificado se ha subido correctamente.",
  immuCertFile: "Archivo del certificado",
  immuSelectFile: "Seleccionar archivo",
  immuFileHint: "PDF, PNG o JPEG (máx. 10 MB)",
  immuFhirTitle: "Registro digital de vacunación",
  immuBundleInfo: "Información del bundle",
  immuResourceType: "Tipo de recurso",
  immuBundleType: "Tipo",
  immuTotalEntries: "Total de entradas",
  immuTimestamp: "Marca de tiempo",
  immuIdentifiers: "Identificadores",
  immuStatus: "Estado",
  immuStatusCompleted: "Completada",
  immuStatusNotDone: "No realizada",
  immuStatusEnteredInError: "Registrada por error",
  immuVaccine: "Vacuna",
  immuPerformedBy: "Realizada por",
  immuSchedule: "Pauta de vacunación",
  immuNotes: "Notas",
  immuRecordedOn: "Registrado el: {date}",
  immuShowRawJson: "Mostrar JSON FHIR sin procesar",
  immuDate: "Fecha",
  immuPhysician: "Médico",
  immuNextVaccination: "Próxima vacunación",
  immuDigitalVerification: "Verificación digital",
  immuQrVerifyHint: "Código QR para la verificación digital del certificado",
  immuIssuingAuthority: "Autoridad emisora",
  immuOrganization: "Organización",
  immuValidUntil: "Válido hasta",
  immuSignatureId: "ID de firma",
  immuSignedOn: "Firmado el",
  immuLegalNoticeTitle: "Aviso legal",
  immuLegalNoticeText:
    "Este certificado cumple el Reglamento Sanitario Internacional (RSI 2005) de la OMS. Su autenticidad puede verificarse mediante el código QR o el número de certificado.",
  immuCreatedOn: "Creado el: {date}",
  immuDownload: "Descargar",
};

const pt: typeof en = {
  apptTabEmergency: "Perfil de emergência",
  apptTabAppointments: "Consultas",
  apptTabCheckin: "Check-in",
  apptTabInsurance: "Seguro",
  apptOptionalHint: "(opcional)",

  apptTitle: "Consultas",
  apptDescription: "Faça a gestão das suas consultas médicas",
  apptLoadFailed: "Não foi possível carregar as consultas",
  apptBook: "Marcar consulta",
  apptUpcomingHeading: "Próximas consultas ({count})",
  apptPastHeading: "Consultas anteriores ({count})",
  apptNoUpcoming: "Sem consultas agendadas",
  apptBookFirst: "Marque a sua primeira consulta",
  apptStatusUpcoming: "Agendada",
  apptStatusCompleted: "Realizada",
  apptNote: "Nota: {note}",
  apptReschedule: "Reagendar",
  apptType: "Tipo de consulta",
  apptTypeCheckup: "Check-up geral",
  apptTypeTravel: "Consulta do viajante",
  apptTypeVaccination: "Vacinação",
  apptTypeFollowUp: "Acompanhamento",
  apptDate: "Data",
  apptTime: "Hora",
  apptNotes: "Notas (opcional)",
  apptNotesPlaceholder: "Necessidades especiais ou notas...",
  apptComingSoon: "A marcação de consultas estará disponível em breve!",

  checkinTitle: "Check-in de viagem",
  checkinDescription:
    "Partilhar dados de saúde de emergência no check-in da viagem",
  checkinOperatorCruise: "Navio de cruzeiro",
  checkinOperatorAirline: "Companhia aérea",
  checkinOperatorHotel: "Hotel",
  checkinScopeBase: "Dados básicos",
  checkinScopeBaseDesc: "Nome, grupo sanguíneo, data de nascimento",
  checkinScopeEmergency: "Dados de emergência",
  checkinScopeEmergencyDesc:
    "Alergias, medicamentos críticos, contacto de emergência",
  checkinScopeVaccinations: "Estado vacinal",
  checkinScopeVaccinationsDesc: "Vacinas válidas (para a entrada)",
  checkinExpiry24h: "24 horas",
  checkinExpiry72h: "72 horas",
  checkinExpiry7d: "7 dias",
  checkinExpired: "Expirado",
  checkinActive: "Ativo",
  checkinTimeLeft: "Faltam {h} h {m} min",
  checkinSelectScope: "Selecione pelo menos uma categoria de dados",
  checkinQrGenerated: "Código QR de check-in gerado!",
  checkinQrFailed: "Não foi possível gerar o código QR",
  checkinPrivacyTitle: "Em conformidade com o RGPD:",
  checkinPrivacyText:
    "É o utilizador que decide que dados são partilhados. Cada acesso fica registado no registo de auditoria.",
  checkinChooseOperator: "Escolha o tipo de operador",
  checkinContinue: "Continuar a configuração",
  checkinHistory: "Check-ins anteriores",
  checkinScanned: "Digitalizado ✓",
  checkinDetails: "{operator} – Detalhes",
  checkinOperatorName: "Nome do operador",
  checkinHotelPlaceholder: "Nome do hotel",
  checkinBookingRef: "Referência da reserva",
  checkinSharedData: "Dados partilhados",
  checkinValidity: "Período de validade",
  checkinGenerating: "A gerar...",
  checkinGenerate: "Gerar código QR",
  checkinQrHeading: "QR de check-in – {operator}",
  checkinBookingRefShort: "Ref. reserva: {ref}",
  checkinQrAlt: "Código QR de check-in",
  checkinCopied: "Copiado!",
  checkinCopyLink: "Copiar ligação",
  checkinStaffScans:
    "O pessoal do check-in lê este código. O acesso fica registado no registo de auditoria.",
  checkinCompleted: "Check-in concluído!",
  checkinConfirm: "Confirmar check-in",
  checkinSuccess: "Check-in efetuado com sucesso!",
  checkinSharedWith: "Os seus dados de saúde foram partilhados com {operator}.",
  checkinAccessExpires: "O acesso expira a {date}.",
  checkinNew: "Novo check-in",
  checkinToAppointments: "Ir para as consultas",

  insTitle: "Pedidos ao seguro",
  insDescription: "Peça repatriamento e prestações do seguro",
  insTypeRepatriation: "Repatriamento",
  insTypeTreatment: "Tratamento médico",
  insTypePrescription: "Receita / medicamentos",
  insTypeOther: "Outro",
  insStatusPending: "Pendente",
  insStatusSubmitted: "Enviado",
  insStatusApproved: "Aprovado",
  insStatusRejected: "Recusado",
  insRequiredError: "O nome da seguradora e a descrição são obrigatórios",
  insSubmitted: "Pedido ao seguro enviado",
  insSubmitFailed: "Não foi possível enviar o pedido",
  insMyRequests: "Os meus pedidos",
  insRefresh: "Atualizar",
  insNew: "Novo pedido",
  insFormTitle: "Novo pedido ao seguro",
  insRequestType: "Tipo de pedido",
  insCompany: "Seguradora",
  insCompanyPlaceholder: "p. ex. Fidelidade Assistance",
  insPolicyNumber: "Número da apólice",
  insDescriptionLabel: "Descrição",
  insDescriptionPlaceholder: "Descreva a sua situação e o que precisa...",
  insEmergencyNote:
    "Numa emergência real, contacte diretamente a sua seguradora. Esta função serve para documentar e enviar pedidos não urgentes.",
  insSubmitting: "A enviar...",
  insSubmit: "Enviar pedido",
  insEmpty: "Sem pedidos",
  insEmptyHint: "Envie o seu primeiro pedido ao seguro.",
  insReference: "Referência: {ref}",

  immuLoadFailed: "Não foi possível carregar as vacinas",
  immuSaveFailed: "Não foi possível guardar a vacina",
  immuDeleteFailed: "Não foi possível eliminar a vacina",
  immuDeleteConfirm:
    "Tem a certeza de que pretende eliminar este registo de vacinação?",
  immuFhirLoadFailed: "Não foi possível carregar o registo digital",
  immuCertLoadFailed: "Não foi possível carregar o certificado internacional",
  immuUploadFailed: "Não foi possível carregar o certificado",
  immuNoCertificate: "Não existe certificado para esta vacina",
  immuDownloadFailed: "Não foi possível transferir o certificado",
  immuDigitalRecord: "Registo digital",
  immuDigitalRecordTitle: "Registo digital de vacinação",
  immuVaccineName: "Nome da vacina",
  immuVaccineNamePlaceholder: "p. ex. vacina de mRNA contra a COVID-19",
  immuVaccineCodePlaceholder: "p. ex. CVX-218",
  immuManufacturerPlaceholder: "p. ex. BioNTech/Pfizer",
  immuLotPlaceholder: "p. ex. FF1234",
  immuDoseNumber: "N.º da dose",
  immuTotalDoses: "Total de doses",
  immuAdminDate: "Data de administração",
  immuExpirationDate: "Data de validade",
  immuNextDueDate: "Próxima data prevista",
  immuAdminLocation: "Local de administração",
  immuLocationPlaceholder: "p. ex. Centro de Saúde de Lisboa",
  immuAdministeredBy: "Administrada por",
  immuAdministratorPlaceholder: "p. ex. Dr. Silva",
  immuEmptyTitle: "Nenhuma vacina encontrada",
  immuEmptyHint: "Adicione o seu primeiro registo de vacinação",
  immuAddFirst: "Adicionar primeira vacina",
  immuDoseOf: "Dose {dose} de {total}",
  immuLot: "Lote: {lot}",
  immuNextDue: "Próxima dose: {date}",
  immuDownloadCert: "Transferir certificado",
  immuUploadCert: "Carregar certificado",
  immuUploadTitle: "Carregar certificado",
  immuCertFileLabel: "Certificado de vacinação (PDF, JPEG, PNG, WebP, HEIC)",
  immuUploading: "A carregar...",
  immuUpload: "Carregar",
  immuFhirBundleTitle: "Bundle de vacinação FHIR R5",
  immuRecordCopied: "Registo copiado para a área de transferência!",
  immuCopyToClipboard: "Copiar para a área de transferência",
  immuCertCopied: "Dados do certificado copiados para a área de transferência!",
  immuName: "Nome",
  immuDateOfBirth: "Data de nascimento",
  immuSex: "Sexo",
  immuSexMale: "Masculino",
  immuSexFemale: "Feminino",
  immuSexOther: "Outro",
  immuSexUnknown: "Desconhecido",
  immuAlgorithm: "Algoritmo",
  immuSignature: "Assinatura",
  immuVerified: "Verificado",
  immuSignedBy: "Assinado por {issuer}",
  immuUnsigned:
    "Não assinado – este servidor não tem uma chave de assinatura configurada",
  immuNotAvailable: "N/D",
  immuNotSpecified: "Não indicado",
  immuUploadVaccCertTitle: "Carregar certificado de vacinação",
  immuFileTypeError: "Só são permitidos ficheiros PDF, PNG e JPEG",
  immuFileTooLarge: "Ficheiro demasiado grande. Máximo de 10 MB",
  immuUploadSuccessTitle: "Carregamento concluído!",
  immuUploadSuccessText: "O certificado foi carregado com sucesso.",
  immuCertFile: "Ficheiro do certificado",
  immuSelectFile: "Selecionar ficheiro",
  immuFileHint: "PDF, PNG ou JPEG (máx. 10 MB)",
  immuFhirTitle: "Registo digital de vacinação",
  immuBundleInfo: "Informações do bundle",
  immuResourceType: "Tipo de recurso",
  immuBundleType: "Tipo",
  immuTotalEntries: "Total de entradas",
  immuTimestamp: "Data e hora",
  immuIdentifiers: "Identificadores",
  immuStatus: "Estado",
  immuStatusCompleted: "Concluída",
  immuStatusNotDone: "Não realizada",
  immuStatusEnteredInError: "Registada por erro",
  immuVaccine: "Vacina",
  immuPerformedBy: "Realizada por",
  immuSchedule: "Esquema vacinal",
  immuNotes: "Notas",
  immuRecordedOn: "Registado em: {date}",
  immuShowRawJson: "Mostrar JSON FHIR em bruto",
  immuDate: "Data",
  immuPhysician: "Médico",
  immuNextVaccination: "Próxima vacinação",
  immuDigitalVerification: "Verificação digital",
  immuQrVerifyHint: "Código QR para a verificação digital do certificado",
  immuIssuingAuthority: "Autoridade emissora",
  immuOrganization: "Organização",
  immuValidUntil: "Válido até",
  immuSignatureId: "ID da assinatura",
  immuSignedOn: "Assinado em",
  immuLegalNoticeTitle: "Aviso legal",
  immuLegalNoticeText:
    "Este certificado está em conformidade com o Regulamento Sanitário Internacional (RSI 2005) da OMS. A sua autenticidade pode ser verificada através do código QR ou do número do certificado.",
  immuCreatedOn: "Criado em: {date}",
  immuDownload: "Transferir",
};

export const visitsStrings: Record<Language, typeof en> = {
  en,
  de,
  fr,
  es,
  pt,
};
